/**
 * The small, quiet pieces: progress, absence, status and hover help.
 *
 * They are grouped because they share one idea — none of them is the thing the
 * user came for, so none of them should shout.
 */

import { Children, cloneElement, useCallback, useEffect, useId, useRef, useState } from 'react'
import type { KeyboardEvent, ReactElement, ReactNode } from 'react'

import styles from './Feedback.module.css'

function cx(...values: Array<string | false | null | undefined>): string {
  return values.filter((value): value is string => typeof value === 'string' && value !== '').join(' ')
}

// ── Spinner ─────────────────────────────────────────────────────────────────

export type SpinnerSize = 'sm' | 'md' | 'lg'

export interface SpinnerProps {
  size?: SpinnerSize
  /**
   * Announced to assistive technology. Omit it when the spinner sits inside
   * something that already says what is happening — a loading button, say —
   * and it becomes decorative instead of a second announcement.
   */
  label?: string
  className?: string
}

export function Spinner({ size = 'md', label, className }: SpinnerProps): ReactElement {
  const described = typeof label === 'string' && label !== ''

  return (
    <span
      className={cx(styles.spinner, styles[size], className)}
      role={described ? 'status' : undefined}
      aria-label={described ? label : undefined}
      aria-hidden={described ? undefined : true}
    />
  )
}

// ── Full-page states ────────────────────────────────────────────────────────

export interface FullPageSpinnerProps {
  label?: string
}

/**
 * Held up while the application works out what it is allowed to show. It
 * exists so that no route ever flashes a login form at somebody who is in fact
 * signed in.
 */
export function FullPageSpinner({ label = 'Loading' }: FullPageSpinnerProps): ReactElement {
  return (
    <div className={styles.fullPage}>
      <Spinner size="lg" label={label} />
    </div>
  )
}

export interface FullPageMessageProps {
  title: string
  description?: ReactNode
  actions?: ReactNode
  icon?: ReactNode
}

/** A recoverable dead end: a crash, a missing route, an unreachable server. */
export function FullPageMessage({
  title,
  description,
  actions,
  icon,
}: FullPageMessageProps): ReactElement {
  return (
    <div className={styles.fullPage}>
      <div className={styles.panel} role="alert">
        {icon !== undefined && icon !== null ? (
          <span className={styles.panelIcon} aria-hidden="true">
            {icon}
          </span>
        ) : null}
        <h1 className={styles.panelTitle}>{title}</h1>
        {description !== undefined && description !== null ? (
          <p className={styles.panelText}>{description}</p>
        ) : null}
        {actions !== undefined && actions !== null ? (
          <div className={styles.panelActions}>{actions}</div>
        ) : null}
      </div>
    </div>
  )
}

// ── Empty state ─────────────────────────────────────────────────────────────

export interface EmptyStateProps {
  title: string
  description?: ReactNode
  icon?: ReactNode
  action?: ReactNode
  className?: string
  /** Fills its container rather than sitting at its natural height. */
  fill?: boolean
}

export function EmptyState({
  title,
  description,
  icon,
  action,
  className,
  fill = false,
}: EmptyStateProps): ReactElement {
  return (
    <div className={cx(styles.empty, fill && styles.emptyFill, className)}>
      {icon !== undefined && icon !== null ? (
        <span className={styles.emptyIcon} aria-hidden="true">
          {icon}
        </span>
      ) : null}
      <p className={styles.emptyTitle}>{title}</p>
      {description !== undefined && description !== null ? (
        <p className={styles.emptyText}>{description}</p>
      ) : null}
      {action !== undefined && action !== null ? (
        <div className={styles.emptyAction}>{action}</div>
      ) : null}
    </div>
  )
}

// ── Badge ───────────────────────────────────────────────────────────────────

export type BadgeTone = 'neutral' | 'info' | 'success' | 'warning' | 'danger'

export interface BadgeProps {
  children: ReactNode
  tone?: BadgeTone
  /** A small filled pill for unread counts, rather than an outlined label. */
  emphasis?: 'soft' | 'solid'
  className?: string
}

export function Badge({
  children,
  tone = 'neutral',
  emphasis = 'soft',
  className,
}: BadgeProps): ReactElement {
  return (
    <span className={cx(styles.badge, styles[tone], styles[emphasis], className)}>{children}</span>
  )
}

// ── Tooltip ─────────────────────────────────────────────────────────────────

export type TooltipPlacement = 'top' | 'bottom' | 'left' | 'right'

export interface TooltipProps {
  label: string
  placement?: TooltipPlacement
  /** Milliseconds of hover before it appears. Keyboard focus shows it at once. */
  delay?: number
  children: ReactElement<{ 'aria-describedby'?: string }>
}

/**
 * Positioned entirely in CSS against the wrapper, which keeps it dependency
 * free. The trade-off is that a tooltip near a viewport edge is not flipped, so
 * it is for short labels on controls that live inside the layout, not for
 * anything that has to survive being pushed against the window.
 */
export function Tooltip({
  label,
  placement = 'top',
  delay = 350,
  children,
}: TooltipProps): ReactElement {
  const id = useId()
  const [open, setOpen] = useState(false)
  const timer = useRef<number | null>(null)

  const clear = useCallback((): void => {
    if (timer.current !== null) {
      window.clearTimeout(timer.current)
      timer.current = null
    }
  }, [])

  useEffect(() => clear, [clear])

  const show = useCallback(
    (immediate: boolean): void => {
      clear()
      if (immediate) {
        setOpen(true)
        return
      }
      timer.current = window.setTimeout(() => setOpen(true), delay)
    },
    [clear, delay],
  )

  const hide = useCallback((): void => {
    clear()
    setOpen(false)
  }, [clear])

  const onKeyDown = (event: KeyboardEvent<HTMLSpanElement>): void => {
    // Escape dismisses the tooltip without disturbing whatever has focus.
    if (event.key === 'Escape' && open) {
      event.stopPropagation()
      hide()
    }
  }

  const child = Children.only(children)
  const describedBy = [child.props['aria-describedby'], open ? id : undefined]
    .filter((value): value is string => typeof value === 'string' && value !== '')
    .join(' ')

  return (
    <span
      className={styles.tooltipAnchor}
      onPointerEnter={() => show(false)}
      onPointerLeave={hide}
      onFocusCapture={() => show(true)}
      onBlurCapture={hide}
      onKeyDown={onKeyDown}
    >
      {cloneElement(child, { 'aria-describedby': describedBy === '' ? undefined : describedBy })}
      <span
        id={id}
        role="tooltip"
        className={cx(styles.tooltip, styles[placement])}
        hidden={!open}
      >
        {label}
      </span>
    </span>
  )
}
